"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { workScopeStatusOptions } from "../../constants/options"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"

const BASE_FIELD_NAME = "home.treeDebrisInfo"
const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const

function TreeDebrisInfoForm() {
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    
    // Watch status fields and claimed by fields
    const treeWorkNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.treeWorkNeeded` })
    const treeClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.treeClaimedBy` })
    const exteriorDebrisRemovalNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.exteriorDebrisRemovalNeeded` })
    const debrisClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.debrisClaimedBy` })
    const heavyEquipmentNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.heavyEquipmentNeeded` })
    const heavyClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.heavyClaimedBy` })
    const goodwillTreeWorkOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillTreeWorkOn` })
    const goodwillExteriorDebrisOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillExteriorDebrisOn` })
    const goodwillHeavyEquipmentOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillHeavyEquipmentOn` })
    const goodwillTreeWorkNeeded = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillTreeWorkNeeded` })
    const goodwillTreeWorkNeededClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillTreeWorkNeededClaimedBy`,
    })
    const goodwillExteriorDebrisRemovalNeeded = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillExteriorDebrisRemovalNeeded`,
    })
    const goodwillExteriorDebrisRemovalNeededClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillExteriorDebrisRemovalNeededClaimedBy`,
    })
    const goodwillHeavyEquipmentNeeded = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillHeavyEquipmentNeeded`,
    })
    const goodwillHeavyEquipmentNeededClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillHeavyEquipmentNeededClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: treeWorkNeeded,
        claimedBy: treeClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.treeClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: exteriorDebrisRemovalNeeded,
        claimedBy: debrisClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.debrisClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: heavyEquipmentNeeded,
        claimedBy: heavyClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.heavyClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillTreeWorkNeeded,
        claimedBy: goodwillTreeWorkNeededClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillTreeWorkNeededClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillTreeWorkOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillExteriorDebrisRemovalNeeded,
        claimedBy: goodwillExteriorDebrisRemovalNeededClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillExteriorDebrisRemovalNeededClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillExteriorDebrisOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillHeavyEquipmentNeeded,
        claimedBy: goodwillHeavyEquipmentNeededClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillHeavyEquipmentNeededClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillHeavyEquipmentOn,
    })
    return (
        <HomeDetailsWrapperAccordion heading="Tree, Exterior Debris & Heavy Equipment" wrapperClassName="two-cols-grid">
            {/* Row 1: Tree Work + Tree Claimed By */}
            <WorkScopeStatusSelect workScopeSlug="tree-work"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Tree Work Needed" toggleName={`${BASE_FIELD_NAME}.goodwillTreeWorkOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.treeWorkNeeded`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Tree Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.treeClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillTreeWorkOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{
                            label: "Tree Work Goodwill",
                            labelStyle: goodwillLabelStyle,
                        }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillTreeWorkNeeded`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Tree Work Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillTreeWorkNeededClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row: Exterior Debris Needed + Debris Claimed By */}
            <WorkScopeStatusSelect workScopeSlug="exterior-debris-removal"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Exterior Debris Removal Needed" toggleName={`${BASE_FIELD_NAME}.goodwillExteriorDebrisOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.exteriorDebrisRemovalNeeded`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Debris Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.debrisClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillExteriorDebrisOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{
                            label: "Exterior Debris Goodwill",
                            labelStyle: goodwillLabelStyle,
                        }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillExteriorDebrisRemovalNeeded`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Exterior Debris Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillExteriorDebrisRemovalNeededClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row: Heavy Equipment Needed + Heavy Equipment Claimed By */}
            <WorkScopeStatusSelect workScopeSlug="heavy-equipment-demo"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Heavy Equipment/Demo Needed" toggleName={`${BASE_FIELD_NAME}.goodwillHeavyEquipmentOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.heavyEquipmentNeeded`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{ label: "Heavy Equipment Claimed By" }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.heavyClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillHeavyEquipmentOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{
                            label: "Heavy Equipment Goodwill",
                            labelStyle: goodwillLabelStyle,
                        }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillHeavyEquipmentNeeded`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Heavy Equipment Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillHeavyEquipmentNeededClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Description full width */}
            <DateStampedTextarea
                inputGroupProps={{
                    label: "Description",
                    wrapperClassName: "two-cols",
                }}
                rows={3}
                placeholder="Describe the tree debris work needed"
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
                includeUserName
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default TreeDebrisInfoForm
